import React, { useState } from 'react';
import { MagnifyingGlass, CaretUp, CaretDown, CaretLeft, CaretRight, CheckSquare, Square, Funnel, X } from '@phosphor-icons/react';

// ─── STATUS BADGE ────────────────────────────────────
const STATUS_THEMES = {
  active: { bg: '#10B981', label: 'Active' },
  verified: { bg: '#10B981', label: 'Verified' },
  published: { bg: '#10B981', label: 'Published' },
  approved: { bg: '#00F0FF', label: 'Approved' },
  pending: { bg: '#F59E0B', label: 'Pending' },
  draft: { bg: '#F59E0B', label: 'Draft' },
  submitted: { bg: '#3B82F6', label: 'Submitted' },
  suspended: { bg: '#EF4444', label: 'Suspended' },
  banned: { bg: '#EF4444', label: 'Banned' },
  rejected: { bg: '#EF4444', label: 'Rejected' },
  archived: { bg: '#555', label: 'Archived' },
  resolved: { bg: '#555', label: 'Resolved' },
  free: { bg: '#555', label: 'Free' },
  pro: { bg: '#9D4CDD', label: 'Pro' },
  premium: { bg: '#F59E0B', label: 'Premium' },
  bronze: { bg: '#CD7F32', label: 'Bronze' },
  silver: { bg: '#C0C0C0', label: 'Silver' },
  gold: { bg: '#FFD700', label: 'Gold' },
  platinum: { bg: '#E5E4E2', label: 'Platinum' },
  true: { bg: '#10B981', label: 'Yes' },
  false: { bg: '#EF4444', label: 'No' },
};

export function StatusBadge({ status, size = 'sm' }) {
  const s = String(status);
  const theme = STATUS_THEMES[s] || { bg: '#555', label: s };
  const cls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-[11px] px-2.5 py-1';
  return (
    <span className={`inline-flex items-center rounded-md font-medium capitalize ${cls}`}
      style={{ backgroundColor: `${theme.bg}15`, color: theme.bg }}
      data-testid={`status-badge-${s}`}>
      {theme.label}
    </span>
  );
}

// ─── FILTER BAR ──────────────────────────────────────
export function FilterBar({ children, searchValue, onSearchChange, onSearch, searchPlaceholder = 'Search...', count, label }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="filter-bar">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#444]" />
        <input
          type="text" placeholder={searchPlaceholder} value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSearch?.()}
          className="w-full bg-[#111118] border border-white/[0.07] rounded-md pl-9 pr-3 py-[7px] text-white text-[13px] focus:border-[#EF4444]/40 focus:outline-none placeholder-[#3a3a3a] transition-colors"
          data-testid="filter-search-input"
        />
      </div>
      {children}
      {(count !== undefined && label) && (
        <span className="text-[11px] text-[#444] ml-auto tabular-nums">{count} {label}</span>
      )}
    </div>
  );
}

// ─── FILTER CHIP ─────────────────────────────────────
export function FilterChip({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-[7px] rounded-md text-[12px] font-medium transition-all border ${
        active
          ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/25'
          : 'bg-[#111118] text-[#555] border-white/[0.07] hover:text-[#999] hover:border-white/[0.12]'
      }`}
      data-testid={testId}>
      {label}
    </button>
  );
}

// ─── DATA TABLE ──────────────────────────────────────
export function DataTable({
  columns, data, loading, emptyMessage = 'No data found', emptyIcon: EmptyIcon,
  selectable, selectedIds = [], onSelectionChange,
  onRowClick, rowTestIdPrefix = 'row',
  page = 0, pageSize = 50, total = 0, onPageChange,
}) {
  const allSelected = data.length > 0 && data.every(d => selectedIds.includes(d.id));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange?.(selectedIds.filter(id => !data.find(d => d.id === id)));
    } else {
      onSelectionChange?.([...new Set([...selectedIds, ...data.map(d => d.id)])]);
    }
  };

  const toggleOne = (id) => {
    onSelectionChange?.(
      selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg overflow-hidden" data-testid="data-table">
      {/* ─── DESKTOP / TABLET TABLE (≥ md) ─────────────────── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.07] bg-[#0a0a11]">
              {selectable && (
                <th className="w-10 px-3 py-2.5">
                  <button onClick={toggleAll} className="text-[#555] hover:text-white">
                    {allSelected ? <CheckSquare size={15} weight="fill" className="text-[#EF4444]" /> : <Square size={15} />}
                  </button>
                </th>
              )}
              {columns.map(col => (
                <th key={col.key} className={`text-left text-[10px] uppercase tracking-wider text-[#444] px-3 py-2.5 font-semibold whitespace-nowrap ${col.className || ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  {selectable && <td className="px-3 py-2.5"><div className="w-4 h-4 bg-white/5 rounded" /></td>}
                  {columns.map(col => (
                    <td key={col.key} className="px-3 py-2.5"><div className="h-4 bg-white/[0.04] rounded w-3/4" /></td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-10 text-center">
                  {EmptyIcon && <EmptyIcon size={28} className="mx-auto text-[#2a2a2a] mb-2" />}
                  <p className="text-[#444] text-[13px]">{emptyMessage}</p>
                </td>
              </tr>
            ) : data.map((row, idx) => (
              <tr key={row.id || idx}
                className={`border-b border-white/[0.04] transition-colors ${onRowClick ? 'cursor-pointer hover:bg-[#EF4444]/[0.02]' : 'hover:bg-white/[0.015]'}`}
                onClick={() => onRowClick?.(row)}
                data-testid={`${rowTestIdPrefix}-${row.id || idx}`}>
                {selectable && (
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => toggleOne(row.id)} className="text-[#555] hover:text-white">
                      {selectedIds.includes(row.id)
                        ? <CheckSquare size={15} weight="fill" className="text-[#EF4444]" />
                        : <Square size={15} />}
                    </button>
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key} className={`px-3 py-2 ${col.cellClass || ''}`}>
                    {col.render ? col.render(row[col.key], row) : (
                      <span className="text-[13px] text-[#999]">{row[col.key] ?? '-'}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ─── MOBILE CARD LIST (< md) ──────────────────────── */}
      <div className="md:hidden divide-y divide-white/[0.04]">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="p-3 space-y-2">
              <div className="h-4 bg-white/[0.04] rounded w-1/2 animate-pulse" />
              <div className="h-3 bg-white/[0.03] rounded w-2/3 animate-pulse" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="px-4 py-10 text-center">
            {EmptyIcon && <EmptyIcon size={28} className="mx-auto text-[#2a2a2a] mb-2" />}
            <p className="text-[#444] text-[13px]">{emptyMessage}</p>
          </div>
        ) : data.map((row, idx) => {
          // Split columns: "_actions" (or anything starting with "_") goes to the
          // right-side action area. Everything else stacks as label/value rows.
          const dataCols = columns.filter(c => !String(c.key).startsWith('_'));
          const actionCols = columns.filter(c => String(c.key).startsWith('_'));
          const isSel = selectedIds.includes(row.id);
          return (
            <div
              key={row.id || idx}
              className={`p-3 transition-colors ${onRowClick ? 'active:bg-[#EF4444]/[0.04]' : ''}`}
              data-testid={`${rowTestIdPrefix}-${row.id || idx}`}
            >
              <div className="flex items-start gap-2">
                {selectable && (
                  <button
                    onClick={() => toggleOne(row.id)}
                    className="mt-1 text-[#555] hover:text-white flex-shrink-0"
                    aria-label="Select row"
                  >
                    {isSel ? <CheckSquare size={17} weight="fill" className="text-[#EF4444]" /> : <Square size={17} />}
                  </button>
                )}
                <div
                  onClick={() => onRowClick?.(row)}
                  className={`flex-1 min-w-0 ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {dataCols.map((col, ci) => {
                    const rendered = col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-');
                    return (
                      <div
                        key={col.key}
                        className={ci === 0 ? 'mb-1' : 'flex items-center gap-2 text-[11px] mt-0.5'}
                      >
                        {ci === 0 ? (
                          <div className="text-[14px] text-white font-medium truncate">{rendered}</div>
                        ) : (
                          <>
                            {col.label && (
                              <span className="text-[9px] uppercase tracking-wider text-[#444] flex-shrink-0">{col.label}:</span>
                            )}
                            <span className="text-[12px] text-[#999] min-w-0 truncate">{rendered}</span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                {actionCols.length > 0 && (
                  <div className="flex flex-col gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {actionCols.map(col => (
                      <div key={col.key}>
                        {col.render ? col.render(row[col.key], row) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06] bg-[#0a0a11]">
          <span className="text-[11px] text-[#444] tabular-nums">
            {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button onClick={() => onPageChange?.(page - 1)} disabled={page === 0}
              className="p-1.5 rounded text-[#555] hover:text-white disabled:opacity-20 disabled:cursor-not-allowed">
              <CaretLeft size={14} />
            </button>
            <button onClick={() => onPageChange?.(page + 1)} disabled={page >= totalPages - 1}
              className="p-1.5 rounded text-[#555] hover:text-white disabled:opacity-20 disabled:cursor-not-allowed">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BULK ACTION BAR ─────────────────────────────────
export function BulkActionBar({ count, actions = [], onClear }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-lg bg-[#EF4444]/[0.06] border border-[#EF4444]/20" data-testid="bulk-action-bar">
      <span className="text-[13px] text-[#EF4444] font-medium tabular-nums">{count} selected</span>
      <div className="flex gap-2 ml-2">
        {actions.map(a => (
          <button key={a.label} onClick={a.onClick}
            className="text-[12px] px-3 py-1.5 rounded-md font-medium transition-colors"
            style={{ backgroundColor: `${a.color || '#EF4444'}15`, color: a.color || '#EF4444' }}
            data-testid={`bulk-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
            {a.label}
          </button>
        ))}
      </div>
      <button onClick={onClear} className="ml-auto text-[#555] hover:text-white">
        <X size={14} />
      </button>
    </div>
  );
}

// ─── DETAIL DRAWER ───────────────────────────────────
export function DetailDrawer({ open, onClose, title, children, width = 'w-[420px]' }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className={`fixed top-0 right-0 h-full w-full sm:max-w-[420px] bg-[#0a0a12] border-l border-white/[0.06] z-50 overflow-y-auto`}
        data-testid="detail-drawer">
        <div className="sticky top-0 bg-[#0a0a12] border-b border-white/[0.06] px-4 sm:px-5 py-3 flex items-center justify-between z-10">
          <h3 className="text-white font-semibold text-sm">{title}</h3>
          <button onClick={onClose} className="text-[#555] hover:text-white p-1" data-testid="drawer-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </>
  );
}

// ─── CONFIRM MODAL ───────────────────────────────────
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm bg-[#111118] border border-white/[0.08] rounded-xl p-6"
        data-testid="confirm-modal">
        <h3 className="text-white font-semibold mb-2">{title}</h3>
        <p className="text-[13px] text-[#888] mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-[#888] hover:text-white">Cancel</button>
          <button onClick={() => { onConfirm?.(); onClose(); }}
            className={`px-4 py-2 rounded-lg text-[13px] font-semibold ${danger ? 'bg-[#EF4444] text-white' : 'bg-white text-black'}`}
            data-testid="confirm-action-btn">
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-10 text-center" data-testid="empty-state">
      {Icon && <Icon size={36} className="mx-auto text-[#222] mb-3" />}
      <p className="text-white font-medium text-[15px] mb-1">{title}</p>
      {description && <p className="text-[13px] text-[#555] mb-4">{description}</p>}
      {action}
    </div>
  );
}

// ─── KPI CARD ────────────────────────────────────────
export function KPICard({ icon: Icon, label, value, accent = '#EF4444', subtext, onClick }) {
  return (
    <div className={`bg-[#0c0c14] border border-white/[0.06] rounded-lg p-3.5 ${onClick ? 'cursor-pointer hover:border-white/[0.12] transition-colors' : ''}`}
      onClick={onClick} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}10` }}>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <span className="text-[10px] text-[#444] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-white tracking-tight tabular-nums">{value}</p>
      {subtext && <p className="text-[10px] text-[#444] mt-0.5">{subtext}</p>}
    </div>
  );
}
